import { CreditCard } from 'lucide-react'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { FeeSource, FeeType, type OrganizationFeeWithDefault } from '@ankor-io/common/types/pay/fee'
import { Button, EmptyState, Heading, Loading, Text } from '@ankor-io/ui-components'

import { MIN_LOADING_DISPLAY_MS } from '@/constants/ui'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useUser } from '@/hooks/useUser'
import {
  useDeleteOrganizationFeesMutation,
  useListOrganizationVesselsQuery,
  useOrganizationFeesQuery,
  useUpdateOrganizationFeesMutation,
} from '@/queries'

import { ConnectedAccountDepositOverrides } from './connected-account-deposit-overrides'
import { OrganizationFeeCard } from './organization-fee-card'

interface OrganizationFeesProps {
  organizationId: string
}

const FEE_CARDS: { source: FeeSource; title: string; description: string }[] = [
  { source: FeeSource.CONVERSION, title: 'Conversion Fee', description: 'Applied to currency conversions.' },
  { source: FeeSource.TRANSFER, title: 'Transfer Fee', description: 'Applied to outgoing transfers.' },
  {
    source: FeeSource.DEPOSIT,
    title: 'Deposit Charge',
    description: 'Applied to deposits. APA connected accounts only.',
  },
]

export function OrganizationFees({ organizationId }: OrganizationFeesProps) {
  const { user } = useUser()
  const { data: organizationFees, isPending, isError, error } = useOrganizationFeesQuery(organizationId)
  const { data: vessels } = useListOrganizationVesselsQuery({ organizationId })

  const { mutateAsync: updateFees, isPending: isUpdating } = useUpdateOrganizationFeesMutation(organizationId)
  const { mutateAsync: deleteFees, isPending: isDeleting } = useDeleteOrganizationFeesMutation(organizationId)

  const feesArray = organizationFees?.fees ?? []

  // Per-source local edit state (undefined = derive from server data).
  const [useGlobalState, setUseGlobalState] = useState<Partial<Record<FeeSource, boolean>>>({})
  const [valueState, setValueState] = useState<Partial<Record<FeeSource, string>>>({})

  const getUserFullName = (): string => {
    if (!user) return 'Unknown'
    const { givenName, familyName } = user
    if (givenName && familyName) return `${givenName} ${familyName}`
    return givenName || familyName || 'Unknown'
  }

  const orgEntryFor = (source: FeeSource) =>
    feesArray.find((fee: OrganizationFeeWithDefault) => fee.feeSource === source && !fee.awxConnectedAccountId)

  const isOrgOverride = (source: FeeSource) => orgEntryFor(source)?.isOverride ?? false
  const orgValue = (source: FeeSource) =>
    orgEntryFor(source)?.feeAmount ?? orgEntryFor(source)?.globalDefault?.feeAmount ?? 0

  const useGlobalDefaultsFor = (source: FeeSource) => useGlobalState[source] ?? !isOrgOverride(source)
  const valueFor = (source: FeeSource) => valueState[source] ?? String(orgValue(source))

  const hasChanges = (source: FeeSource) => {
    const useGlobalDefaults = useGlobalDefaultsFor(source)
    if (useGlobalDefaults !== !isOrgOverride(source)) return true
    if (!useGlobalDefaults) return valueFor(source) !== String(orgValue(source))
    return false
  }

  const resetSource = (source: FeeSource) => {
    setUseGlobalState((current) => {
      const next = { ...current }
      delete next[source]
      return next
    })
    setValueState((current) => {
      const next = { ...current }
      delete next[source]
      return next
    })
  }

  const handleToggle = (source: FeeSource) => {
    setUseGlobalState((current) => ({ ...current, [source]: !useGlobalDefaultsFor(source) }))
  }

  const anyChanges = FEE_CARDS.some((card) => hasChanges(card.source))

  const handleSaveAll = async () => {
    const upserts: {
      feeSource: FeeSource
      feeType: FeeType
      feeAmount: number
      updatedByName: string
      updatedReason: string
    }[] = []
    const deletes: { feeSource: FeeSource }[] = []

    for (const { source, title } of FEE_CARDS) {
      if (!hasChanges(source)) continue

      if (useGlobalDefaultsFor(source)) {
        // Switching back to the global default removes the org override.
        if (isOrgOverride(source)) deletes.push({ feeSource: source })
        continue
      }

      const amount = parseFloat(valueFor(source))
      if (isNaN(amount) || amount < 0 || amount > 100) {
        toast.error(`${title} must be a number between 0 and 100`)
        return
      }
      upserts.push({
        feeSource: source,
        feeType: FeeType.PERCENTAGE,
        feeAmount: amount,
        updatedByName: getUserFullName(),
        updatedReason: 'Updated via Lighthouse admin interface',
      })
    }

    try {
      if (upserts.length > 0) await updateFees({ fees: upserts })
      if (deletes.length > 0) await deleteFees(deletes)
      FEE_CARDS.forEach((card) => resetSource(card.source))
      toast.success('Pricing updated successfully')
    } catch (err) {
      toast.error(`Failed to update pricing: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  // Only APA connected accounts can carry deposit charges.
  const apaAccounts = useMemo(
    () =>
      (vessels?.vessels ?? []).flatMap((entry) =>
        entry.connectedAccounts
          .filter((account) => account.type === AwxConnectedAccountType.APA)
          .map((account) => ({ id: account.id, name: account.name, vesselName: entry.vessel.name })),
      ),
    [vessels],
  )

  const depositOverrides = useMemo(
    () =>
      feesArray
        .filter((fee: OrganizationFeeWithDefault) => fee.feeSource === FeeSource.DEPOSIT && fee.awxConnectedAccountId)
        .map((fee: OrganizationFeeWithDefault) => ({
          connectedAccountId: fee.awxConnectedAccountId as string,
          feeAmount: fee.feeAmount,
        })),
    [feesArray],
  )

  const handleAddDepositOverride = async (connectedAccountId: string, feeAmount: number) => {
    try {
      await updateFees({
        fees: [
          {
            feeSource: FeeSource.DEPOSIT,
            feeType: FeeType.PERCENTAGE,
            feeAmount,
            awxConnectedAccountId: connectedAccountId,
            updatedByName: getUserFullName(),
            updatedReason: 'Updated via Lighthouse admin interface',
          },
        ],
      })
      toast.success('Deposit charge added')
    } catch (err) {
      toast.error(`Failed to add deposit charge: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleDeleteDepositOverride = async (connectedAccountId: string) => {
    try {
      await deleteFees([{ feeSource: FeeSource.DEPOSIT, connectedAccountId }])
      toast.success('Deposit charge removed')
    } catch (err) {
      toast.error(`Failed to remove deposit charge: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const showLoader = useMinimumDisplayTime(isPending, MIN_LOADING_DISPLAY_MS)

  // Error toast is handled globally by QueryProvider; the component still handles error UI state.
  if (showLoader) {
    return <Loading variant="spinner" />
  }
  if (isError) {
    return (
      <EmptyState
        heading="Failed to load fees"
        subheading={error?.message || 'An error occurred while loading organization fees.'}
        icon={<CreditCard className="mx-auto size-12 text-gray-400" />}
      />
    )
  }
  if (!organizationFees) {
    return (
      <EmptyState
        heading="No fees found"
        subheading="Get started by creating a new fee."
        icon={<CreditCard className="mx-auto size-12 text-gray-400" />}
      />
    )
  }

  const isSubmitting = isUpdating || isDeleting

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <Heading level={4}>Pricing Configuration</Heading>
          <Text className="text-sm text-gray-600 dark:text-gray-400">Manage the fees for your organization</Text>
        </div>
        <Button
          type="button"
          onClick={handleSaveAll}
          disabled={!anyChanges || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {FEE_CARDS.map(({ source, title, description }) => (
          <OrganizationFeeCard
            key={source}
            title={title}
            description={description}
            value={valueFor(source)}
            onValueChange={(value) => setValueState((current) => ({ ...current, [source]: value }))}
            globalDefaultValue={orgEntryFor(source)?.globalDefault?.feeAmount ?? 0}
            useGlobalDefaults={useGlobalDefaultsFor(source)}
            onToggleGlobalDefaults={() => handleToggle(source)}
          >
            {source === FeeSource.DEPOSIT && (
              <ConnectedAccountDepositOverrides
                accounts={apaAccounts}
                overrides={depositOverrides}
                isSubmitting={isSubmitting}
                onAdd={handleAddDepositOverride}
                onDelete={handleDeleteDepositOverride}
                onValidationError={(message) => toast.error(message)}
              />
            )}
          </OrganizationFeeCard>
        ))}
      </div>
    </div>
  )
}

export default OrganizationFees
